import { Extension, type Editor } from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { emptyBulletCuts, emptyBullets, type TextRange } from './copyEditQuote';

/** A finding's words, or every empty bullet (`bullets`): those can't be marked as text. */
type Focus = TextRange & { bullets?: boolean };

const focusKey = new PluginKey<Focus | null>('copyEditFocus');

const bulletsFocus = (items: TextRange[]): Focus | null =>
  items.length ? { from: items[0].from, to: items[items.length - 1].to, bullets: true } : null;

/** The highlight on the text a finding is about: follows edits, never saved in the body. */
export const CopyEditFocus = Extension.create({
  name: 'copyEditFocus',
  addProseMirrorPlugins() {
    return [
      new Plugin<Focus | null>({
        key: focusKey,
        state: {
          init: () => null,
          apply(tr, value) {
            const next = tr.getMeta(focusKey) as Focus | null | undefined;
            if (next !== undefined) return next;
            if (!value || !tr.docChanged) return value;
            if (value.bullets) return bulletsFocus(emptyBullets(tr.doc));
            const from = tr.mapping.map(value.from, 1);
            const to = tr.mapping.map(value.to, -1);
            return from < to ? { from, to } : null;
          },
        },
        props: {
          decorations(state) {
            const range = focusKey.getState(state);
            if (range?.bullets) {
              return DecorationSet.create(
                state.doc,
                emptyBullets(state.doc).map((r) =>
                  Decoration.node(r.from, r.to, { class: 'ink-ce-mark-row' })
                )
              );
            }
            return range
              ? DecorationSet.create(state.doc, [
                  Decoration.inline(range.from, range.to, { class: 'ink-ce-mark' }),
                ])
              : null;
          },
        },
      }),
    ];
  },
});

export const focusedRange = (editor: Editor) => focusKey.getState(editor.state) ?? null;

export function setFocusedRange(editor: Editor, range: TextRange | null) {
  editor.view.dispatch(editor.state.tr.setMeta(focusKey, range));
}

/** Highlight the empty bullets; false when there are none. */
export function focusEmptyBullets(editor: Editor) {
  const focus = bulletsFocus(emptyBullets(editor.state.doc));
  if (focus) setFocusedRange(editor, focus);
  return Boolean(focus);
}

/** Take the empty bullets out, last first so the earlier places still hold. */
export function removeEmptyBullets(editor: Editor) {
  const { tr } = editor.state;
  emptyBulletCuts(editor.state.doc)
    .reverse()
    .forEach((cut) => tr.delete(cut.from, cut.to));
  editor.view.dispatch(tr.setMeta(focusKey, null));
}

/** Put the suggestion where the highlight is. It takes the marks of the text it replaces. */
export function applyAtFocus(editor: Editor, suggestion: string) {
  const range = focusedRange(editor);
  if (!range) return false;
  editor.view.dispatch(
    editor.state.tr.insertText(suggestion, range.from, range.to).setMeta(focusKey, null)
  );
  return true;
}
